import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Box, MapPin, Truck, Shield, Clock, Navigation, ChevronRight, Search, ArrowRight } from "lucide-react";
import { OrganizationSchema, WebSiteSchema, ServiceSchema, FAQSchema, HowToSchema, SpeakableSchema, setCanonical, setMetaTags } from "@/components/seo/SchemaMarkup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAJINI_URL = "https://storajini.com";

const SERVICE_TYPES = [
  { type: "storage", label: "Storage", icon: Box, color: "blue", desc: "Find secure storage spaces near you" },
  { type: "parking", label: "Parking", icon: MapPin, color: "emerald", desc: "Rent affordable parking spots" },
  { type: "moving", label: "Moving", icon: Truck, color: "amber", desc: "Get relocation tips and storage" },
];

const FEATURES = [
  { icon: Shield, title: "Secure & Verified", text: "All storage and parking spaces listed on Storajini are verified for safety and security." },
  { icon: Clock, title: "Flexible Terms", text: "Rent by the day, week, or month. No long-term commitments or hidden fees." },
  { icon: Navigation, title: "Nationwide Coverage", text: "Find spaces in 150+ cities across the Philippines, from Metro Manila to Mindanao." },
];

const FAQS = [
  { question: "What is Storajini?", answer: "Storajini is a peer-to-peer storage and parking rental platform in the Philippines. It connects people who need storage or parking with those who have unused space to rent out." },
  { question: "How do I find storage near me?", answer: "Visit storajini.com and use the search tool. You can filter by city, space type, size, and price to find the perfect storage space near your location." },
  { question: "How much does storage cost in the Philippines?", answer: "Storage prices vary by city and space type. Rates typically range from PHP 200 per day for small spaces to PHP 2,000+ per day for large warehouses. Monthly rentals offer the best value." },
  { question: "Can I rent out my unused space?", answer: "Yes! If you have an empty garage, spare room, parking slot, or warehouse, you can list it on Storajini and start earning passive income. Creating a listing is free." },
  { question: "Is it safe to store my belongings in rented spaces?", answer: "Storajini verifies all listed spaces for quality and security. We recommend visiting the space before renting and discussing security measures with the host directly." },
  { question: "What types of spaces are available?", answer: "You can find a variety of spaces including spare rooms, garages, warehouses, covered parking, open parking, and more. Each listing includes details about size, features, and pricing." },
];

const HOW_IT_WORKS_RENTERS = [
  { title: "Search & Browse", text: "Use Storajini's search tool to find storage or parking spaces near you. Filter by city, size, price, and features." },
  { title: "Contact & Book", text: "Send an inquiry to the host and arrange your rental. Discuss terms, access, and any special requirements." },
  { title: "Move In & Store", text: "Get access to your space and start storing your belongings or parking your vehicle safely and affordably." },
];

const HOW_IT_WORKS_HOSTS = [
  { title: "List Your Space", text: "Create a free listing on Storajini with photos, description, and your pricing. It takes just minutes." },
  { title: "Manage Inquiries", text: "Receive and respond to rental requests from potential renters through the platform's messaging system." },
  { title: "Earn Money", text: "Start earning passive income from your unused garage, spare room, or parking slot with flexible rental terms." },
];

export default function HomePage() {
  const [cities, setCities] = useState([]);
  const [searchCity, setSearchCity] = useState("");

  useEffect(() => {
    setCanonical("/");
    setMetaTags({
      title: "Storajini Guide | Storage, Parking & Moving Solutions in the Philippines",
      description: "Find the best storage, parking, and moving solutions across the Philippines. Compare options in 150+ cities. Your complete guide to Storajini.com.",
    });
    axios.get(`${API}/cities?limit=500`).then(r => setCities(r.data.cities)).catch(() => {});
  }, []);

  // Top 12 cities for 2 clean rows of 6
  const TOP_SLUGS = ["manila", "quezon-city", "makati", "cebu-city", "davao-city", "taguig", "iloilo-city", "bacolod", "cagayan-de-oro", "baguio", "zamboanga-city", "angeles"];
  
  const filteredCities = searchCity
    ? cities.filter(c => c.name.toLowerCase().includes(searchCity.toLowerCase()))
    : cities.filter(c => TOP_SLUGS.includes(c.slug));

  return (
    <div data-testid="home-page">
      <OrganizationSchema />
      <WebSiteSchema />
      <ServiceSchema cityName="Philippines" serviceType="Storage & Parking" description="Find the best storage, parking, and moving solutions across the Philippines." />
      <FAQSchema faqs={FAQS} />
      <HowToSchema name="How to Find Storage in the Philippines" steps={HOW_IT_WORKS_RENTERS} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />

      {/* Hero */}
      <section data-testid="hero-section" className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 z-0" style={{ backgroundImage: 'url("/hero-background.jpg")', backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
                Got stuff?<br />Need space?
              </h1>
              <p className="speakable-intro text-xl text-gray-200 font-medium mb-3">
                Rent storage & parking from people in your area across the Philippines.
              </p>
              <p className="text-lg text-gray-400 mb-8">
                Or turn your empty garage, spare room, or parking slot into income.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={`${STORAJINI_URL}/search`}
                 
                 
                  data-testid="hero-find-space-btn"
                  className="inline-flex items-center justify-center px-7 py-4 bg-amber-500 text-white text-base font-bold rounded-lg hover:bg-amber-600 transition-all hover:shadow-lg hover:shadow-amber-500/25"
                >
                  I need space <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <a
                  href={`${STORAJINI_URL}/list-space`}
                 
                 
                  data-testid="hero-list-space-btn"
                  className="inline-flex items-center justify-center px-7 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white text-base font-bold rounded-lg hover:bg-white/20 transition-all"
                >
                  I have space to rent
                </a>
              </div>
            </div>

            <div className="hidden lg:block animate-fade-in-up stagger-2">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
                <div className="space-y-6">
                  {SERVICE_TYPES.map((s) => (
                    <div key={s.type} className={`${s.type !== "storage" ? "border-t border-white/20 pt-6" : ""}`}>
                      <div className="flex items-center gap-3 mb-1">
                        <s.icon className="w-6 h-6 text-amber-400" />
                        <span className="text-2xl font-bold text-white">{s.label}</span>
                      </div>
                      <p className="text-gray-300 text-sm ml-9">{s.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick City Search */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Find Solutions in Your City</h2>
            <p className="text-gray-500 text-lg">Browse storage, parking, and moving guides for 150+ Philippine cities</p>
          </div>

          <div className="max-w-md mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                data-testid="city-search-input"
                type="text"
                placeholder="Search cities..."
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {filteredCities.map((city) => (
              <Link
                key={city.slug}
                to={`/storage/${city.slug}`}
                data-testid={`city-card-${city.slug}`}
                className="group p-4 rounded-xl border border-gray-100 hover:border-amber-200 hover:bg-amber-50 transition-all text-center"
              >
                <p className="font-semibold text-gray-900 group-hover:text-amber-600 text-sm">{city.name}</p>
                <p className="text-xs text-gray-400 mt-1">{city.region}</p>
              </Link>
            ))}
          </div>

          {searchCity && filteredCities.length === 0 && (
            <p className="text-center text-gray-400 mt-6">No cities found matching "{searchCity}"</p>
          )}

          {/* Can't find your city? banner */}
          <Link
            to="/all-cities"
            data-testid="cant-find-city-banner"
            className="mt-8 block bg-amber-50 border border-amber-200 rounded-xl p-5 text-center hover:bg-amber-100 transition-colors group"
          >
            <p className="text-amber-800 font-semibold">Can't find your city?</p>
            <p className="text-amber-600 text-sm mt-1 group-hover:underline">Click here to search for storage & parking anywhere in the Philippines →</p>
          </Link>
        </div>
      </section>

      {/* Service Types */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">What Are You Looking For?</h2>
            <p className="text-gray-500 text-lg">Choose a category to explore city-by-city guides</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {SERVICE_TYPES.map((service) => (
              <a
                key={service.type}
                href={`${STORAJINI_URL}/search`}
               
               
                data-testid={`service-card-${service.type}`}
                className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-amber-200 hover:shadow-lg transition-all"
              >
                <div className="w-14 h-14 bg-amber-50 rounded-xl flex items-center justify-center mb-5 group-hover:bg-amber-100 transition-colors">
                  <service.icon className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.label} Solutions</h3>
                <p className="text-gray-500 mb-4">{service.desc} in 150+ Philippine cities. Compare options and find the best deals.</p>
                <span className="inline-flex items-center text-amber-600 font-semibold text-sm group-hover:gap-2 transition-all">
                  Explore {service.label} <ChevronRight className="w-4 h-4 ml-1" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Why Choose Storajini?</h2>
            <p className="text-gray-500 text-lg">Whether you need storage or have space to rent, we've got you covered.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {FEATURES.map((f, i) => (
              <div key={i} className="rounded-xl p-6 bg-gray-50 hover:bg-amber-50 transition-colors">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <f.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">How It Works</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-12">
            {[{ title: "For Renters", steps: HOW_IT_WORKS_RENTERS }, { title: "For Hosts", steps: HOW_IT_WORKS_HOSTS }].map((group) => (
              <div key={group.title}>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{group.title}</h3>
                <div className="space-y-6">
                  {group.steps.map((step, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-semibold text-sm">{i + 1}</span>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">{step.title}</h4>
                        <p className="text-gray-500 mt-1">{step.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="py-16 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {FAQS.map((faq, i) => (
              <details key={i} className="group bg-gray-50 rounded-xl border border-gray-100" data-testid={`faq-item-${i}`}>
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                  <span className="font-semibold text-gray-900 text-sm pr-4">{faq.question}</span>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-6 pb-4">
                  <p className="text-gray-500 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-amber-500">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-amber-100 text-lg mb-8">Find storage or parking in your city, or start earning from your unused space.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href={`${STORAJINI_URL}/search`}
             
             
              data-testid="cta-find-storage-btn"
              className="px-8 py-4 bg-white text-amber-600 font-bold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Find Storage Now
            </a>
            <a
              href={`${STORAJINI_URL}/list-space`}
             
             
              data-testid="cta-list-space-btn"
              className="px-8 py-4 bg-amber-600 text-white font-bold rounded-lg border-2 border-amber-500 hover:bg-amber-700 transition-colors"
            >
              Start Earning Today
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
